import type { SavedBackground } from '@/api/types/background'
import type { UndoAction } from '@/lib/history/types/undo-action'
import type { BackgroundDropSlot } from '@/lib/backgrounds/types/background-drop-slot'
import {
  type SavedSpreadBackgrounds,
  buildSavedBackgroundTintRequest,
} from '@/lib/backgrounds/saved-spread-backgrounds'
import type { SpreadBackgroundsRestorePlan } from './types/spread-backgrounds-restore-plan'

const SLOTS: readonly BackgroundDropSlot[] = ['double', 'left', 'right']

function savedBackgroundsEqual(a?: SavedBackground, b?: SavedBackground): boolean {
  if (!a || !b) return !a && !b

  return (
    a.id === b.id &&
    a.tint === b.tint &&
    a.type === b.type &&
    a.colorId === b.colorId &&
    a.imageId === b.imageId
  )
}

/**
 * A background undo step recorded on a spread other than `currentSpreadId`. It can't be replayed
 * from here: that spread's checkpoint was exited when the user left it, and writing to it would
 * re-register the session on it (and repoint the session's spread cross-reference) server-side.
 */
export function isBackgroundStepForOtherSpread(
  action: UndoAction,
  currentSpreadId: string
): boolean {
  return action.type === 'spreadBackgrounds' && action.spreadId !== currentSpreadId
}

/** 4xx statuses that are still worth retrying: timeout, conflict, locked, rate-limited. */
const RETRYABLE_CLIENT_STATUSES = new Set([408, 409, 423, 429])

/**
 * Whether a failed background write is worth retrying. A 4xx (other than the transient ones) means
 * the server rejected the request and would again; anything else — a network error, a 5xx — may
 * pass next time. Reads `status` structurally (as `HttpError` carries it) so `lib/` needn't import
 * the HTTP layer.
 */
export function isRetryableBackgroundWriteError(error: unknown): boolean {
  const status =
    typeof error === 'object' && error !== null && 'status' in error
      ? (error as { status: unknown }).status
      : undefined

  if (typeof status !== 'number' || status < 400 || status >= 500) return true

  return RETRYABLE_CLIENT_STATUSES.has(status)
}

/** True when both maps hold the same background, tint and type in every slot. */
export function savedSpreadBackgroundsEqual(
  a: SavedSpreadBackgrounds,
  b: SavedSpreadBackgrounds
): boolean {
  return SLOTS.every((slot) => savedBackgroundsEqual(a[slot], b[slot]))
}

/**
 * The writes that take a spread's backgrounds from `current` to `target` (an undo/redo replay).
 * Deletes run before PUTs: the backend applies the same double-vs-single exclusivity as
 * `applyBackgroundPlacement`, so clearing first can never wipe a slot a PUT just wrote.
 */
export function planSpreadBackgroundsRestore(
  current: SavedSpreadBackgrounds,
  target: SavedSpreadBackgrounds
): SpreadBackgroundsRestorePlan {
  const plan: SpreadBackgroundsRestorePlan = { deleteSlots: [], putRequests: [], skippedSlots: [] }

  for (const slot of SLOTS) {
    const from = current[slot]
    const to = target[slot]

    if (savedBackgroundsEqual(from, to)) continue

    if (!to) {
      plan.deleteSlots.push(slot)
      continue
    }

    const request = buildSavedBackgroundTintRequest(to, slot, to.tint)

    if (request) {
      plan.putRequests.push(request)
    } else {
      plan.skippedSlots.push(slot)
    }
  }

  return plan
}
